// @flow
import React from 'react';
import classnames from 'classnames';

import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import { Countdown, type CountdownProps } from 'Components/frameworks/countdown/Countdown.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';

export type PromoBannerProps = {
  bgColor?: Colors,
  className?: string,
  closeLabel: string,
  countdown?: CountdownProps,
  description?: string,
  iconName?: IconName,
  innerRef?: string,
  label?: string,
  link: {|
    target?: string,
    url: string,
  |},
  onClose: () => void,
  title: string,
  titleColor: Colors,
  trackingData?: TrackingData,
  trackingDataClose?: TrackingData,
};

export function PromoBanner(props: PromoBannerProps) {
  const {
    bgColor = COLORS.GRENADINE,
    className,
    closeLabel,
    countdown,
    description,
    iconName,
    innerRef,
    label,
    link,
    onClose,
    title,
    titleColor = COLORS.WHITE,
    trackingData,
    trackingDataClose,
  } = props;

  if (!title) return null;

  return (
    <aside
      className={classnames(className, `bg-${bgColor} px-4 relative shadow w-full`)}
      ref={innerRef}
    >
      <TrackClick as={DynamicLink} {...link} trackingData={trackingData} className="block">
        <div className="flex flex-col lg:flex-row items-center justify-center -mx-1 py-3">
          {countdown && <Countdown {...countdown} size="small" className="mb-2 lg:mb-0 mx-1" />}
          <div className="flex flex-col lg:flex-row items-center lg:items-baseline justify-center text-center lg:text-initial text-white">
            {!!iconName && (
              <Icon
                className="mx-1 hidden lg:block self-center"
                color={COLORS.WHITE}
                name={iconName}
                width="2.2rem"
              />
            )}
            {!!label && (
              <p
                className="reset-paragraph hidden font-brand lg:block mx-2"
                data-testid="PromoBanner-label"
              >
                {label}
              </p>
            )}
            <h2
              className={`reset-heading font-brand text-lg lg:text-2xl mx-2 text-${titleColor} text-shadow underline uppercase`}
              data-testid="PromoBanner-title"
            >
              {title}
            </h2>
            {!!description && (
              <p
                className="reset-paragraph mt-1 lg:mt-0 mx-2 text-sm"
                data-testid="PromoBanner-description"
              >
                {description}
              </p>
            )}
          </div>
        </div>
      </TrackClick>
      <TrackClick
        as="button"
        className="reset-button absolute leading-none rtl:mr-2 p-3 top-0 rtl:right-0"
        onClick={onClose}
        trackingData={trackingDataClose}
        type="button"
      >
        <span className="a11y">{closeLabel}</span>
        <Icon color={COLORS.WHITE} name={ICONS.CROSS} width="0.625rem" />
      </TrackClick>
    </aside>
  );
}
